// eslint-disable-next-line vue/no-invalid-v-if
require("es6-promise").polyfill();
import axios from "axios";
import store from "@/store";
import Session from "@/classes/Session";
import { apiUrl } from "@/config/config";

// Abstract class, meant to be extended, no instancieated
class Api {
  constructor(version = "v1") {
    let jwt = Session.getJwtCookie();

    this.http = axios.create({
      baseURL: apiUrl + version + "/",
      headers: {
        "Content-Type": "application/json"
      }
    });

    if (jwt === undefined) {
      Session.logout();
      jwt = "";
    } else {
      this.set_jwt(jwt);
    }

    this.page_length = 25;
  }

  async getProject(params) {
    let response = await this.http.get("projects" + "/" + params);
    return response.data.resource;
  }

  async getLead(params) {
    let response = await this.http.get("leads" + "/" + params);
    return response.data.resource;
  }

  async getBusinessKits(params) {
    let response;
    if (params) {
      response = await this.http.get("businesskits" + "/" + params);
    } else {
      response = await this.http.get("businesskits");
    }

    return response.data.resource;
  }

  async getUserInfo(params) {
    let response = await this.http.get("users" + "/" + params);
    return response.data.resource;
  }

  // Returns multiple entities based on filter. Watch out for browsers character limit
  async getMultiple(entity, filter = "id", value, representer = "full", enforceAllstatuses='false', enforceArchived='false') {
    const response = await this.http.get(
      `${entity}?filter_by=${filter}&filter_value=${value}&representer=${representer}&disable_pagination=true&enforce_all_statuses=${enforceAllstatuses}&enforce_archived=${enforceArchived}`
    );
    return response.data.resources;
  }

  searchAutoComplete(params) {
    if (!params.per_page) {
      params.per_page = 50;
    }
    return new Promise((resolve, reject) => {
      this.http
        .get("/companies/search", { params })
        .then(response => {
          // eslint-disable-next-line no-debugger
          resolve(response.data.resources);
        })
        .catch(e => {
          this.handleError(e);
          reject(e);
        });
    });
  }

  set_jwt(jwt_token) {
    this.http.defaults.headers.common["Authorization"] = "Bearer " + jwt_token;
  }

  model_name() {
    return this.constructor.name.toLowerCase();
  }

  async all(params = {}) {

    try {
      if (!params.per_page) {
        params.per_page = this.page_length
      }

      let { data } = await this.http.get(this.path, { params })

      if (data.resources && !Array.isArray(data.resources)) {
        throw 'Non-array returned from list endpoint'
      }

      if (!data.resources) {
        data.resources = []
      }

      return data
    } catch (e) {
      this.handleError(e)
      throw e
    }
  }

  search(params, searchQuery) {
    if (!params.per_page) {
      params.per_page = this.page_length;
    }
    params.query = searchQuery;
    return new Promise((resolve, reject) => {
      this.http
        .get(this.path + "/search", { params })
        .then(response => {
          resolve(response.data);
        })
        .catch(e => {
          this.handleError(e);
          reject(e);
        });
    });
  }

  // retrieve all the elements formatted for easy-vue-form-field collections
  // @params name : the field that will be displayed in the select (default: 'name')
  all_for_association(name = "name", params = {}) {
    if (!params.per_page) {
      params.per_page = 1000;
    }
    if (!params.sort_by) {
      params.sort_by = name;
      params.sort_order = "ASC";
    }
    return new Promise((resolve, reject) => {
      this.http
        .get(this.path, { params })
        .then(response => {
          let finalArray = [];
          response.data.resources.forEach(function(item) {
            finalArray.push({ value: item.id, name: eval("item." + name) });
          });
          resolve(finalArray);
        })
        .catch(e => {
          console.log(e);
          // this.handleError(e)
          reject(e);
        });
    });
  }

  show(id) {
    let id_name = this.model_name() + "_id";
    return new Promise((resolve, reject) => {
      this.http
        .get(this.path + "/" + id)
        .then(response => {
          resolve(response.data.resource);
        })
        .catch(e => {
          this.handleError(e);
          reject(e);
        });
    });
  }

  async myProfile() {
    try {
      let response = await this.http.get("users/me");
      return response.data.resource;
    } catch (err) {
      this.handleError(err);
      throw err;
    }
  }

  async userName(id) {
    try {
      let response = await this.http.get("users" + "/" + id);
      return response.data.resource.display_name;
    } catch (err) {
      this.handleError(err);
      throw err;
    }
  }

  autofillForm(id) {
    let id_name = this.model_name() + "_id";
    return new Promise((resolve, reject) => {
      this.http
        .get("leads" + "/" + id)
        .then(response => {
          resolve(response.data.resource);
        })
        .catch(e => {
          this.handleError(e);
          reject(e);
        });
    });
  }

  statistics(id, params = {}) {
    let id_name = this.model_name() + "_id";
    return new Promise((resolve, reject) => {
      this.http
        .get(this.path + "/" + id + "/statistics", params)
        .then(response => {
          resolve(response.data);
        })
        .catch(e => {
          this.handleError(e);
          reject(e);
        });
    });
  }

  post(data) {
    return new Promise((resolve, reject) => {
      if (data.id) {
        this.http
          .put(this.path + "/" + data.id, data)
          .then(response => {
            resolve(response.data.resource);
          })
          .catch(e => {
            this.handleError(e);
            reject(e);
          });
      } else {
        this.http
          .post(this.path, data)
          .then(response => {
            resolve(response.data.resource);
          })
          .catch(e => {
            this.handleError(e);
            reject(e);
          });
      }
    });
  }

  delete(id) {
    return new Promise((resolve, reject) => {
      this.http
        .delete(this.path + "/" + id)
        .then(response => {
          resolve(response.data.resource);
        })
        .catch(e => {
          this.handleError(e);
          reject(e);
        });
    });
  }

  // Ajax error handling
  handleError(error) {
    var message = "";
    if (error.response) {
      switch (error.response.status) {
        case 404:
          return error.response;
        case 403:
          if (
            error.response.data.errors[0].code.includes(
              "authorization.plan.forbidden"
            )
          ) {
            this.handlePlanNotAllowed();
          }
          return error.response;
        case 422:
          // Unprocessable entity
          return error.response;
        case 401:
          this.handleUnauthorized(error);
          break;
        default:
          store.dispatch("routerGo", -1);
          message = "errors." + error.response.data.errors[0].code;
          store.dispatch("addAxiosError", message);
          break;
      }
    } else if (error.request) {
      // The request was made but no response was received
      message.push("errors." + error.response.data.errors[0].code);
      router.go(-1);
      store.dispatch("addAxiosError", message[0]);
    } else {
      // Something happened in setting up the request that triggered an Error
      // Should not happen
      console.log("Error", error.message);
    }
  }

  // Handle an unauthorized error.
  // If no special case is found send user to login screen with error.
  handleUnauthorized(error) {
    let code = error.response.data.errors[0].code;
    switch (code) {
      case "authentication.inactive_plan":
        store.commit("SET_CURRENT_ORGANIZATION_PLAN_ACTIVE", false);
        break;
      default:
        store.dispatch("redirectTo", {
          name: "login",
          query: { source: "logout" }
        });
    }
  }

  // Show modal.
  handlePlanNotAllowed() {
    store.commit("SET_NOT_ALLOWED_MODAL_SHOWN", true);
  }
}

export default Api;



// WEBPACK FOOTER //
// ./src/api/Api.js